import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@reflex/ui/components/ui/tabs';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { CommandBlock } from '@reflex/ui/components/content/CommandBlock';
import {
  ClaudeMaxCredentialSchema,
  CredentialAliasSchema,
  type ClaudeMaxCredential,
} from '@reflex/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';

const PLATFORM_COMMANDS = {
  mac: `security find-generic-password -s "Claude Code-credentials" -a "$USER" -w | pbcopy`,
  linux: `cat ~/.claude/.credentials.json`,
  windows: `Get-Content $HOME\\.claude\\.credentials.json`,
} as const;
type Platform = keyof typeof PLATFORM_COMMANDS;

const credentialDialogContentClass =
  'flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-[500px]';
const credentialDialogBodyClass = 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4 pr-1';
const credentialTextareaClass =
  'max-h-[40vh] min-h-[180px] w-full min-w-0 max-w-full resize-y overflow-auto font-mono text-sm';
/** High-visibility caution strip under the paste step (Alert has no warning variant). */
const credentialInfoAlertClass =
  'border-2 border-status-warning bg-status-warning/40 text-foreground [&>svg]:text-status-warning *:data-[slot=alert-description]:text-foreground';
const credentialInfoCodeClass = 'rounded bg-status-warning/50 px-1 py-0.5 font-mono';

export interface RegisterClaudeMaxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fired after a successful registration, with the `mps_*` id of the
   * subscription that was written so callers can act on it (select it in a
   * form, re-point a running agent at it). Null when the server could not
   * mirror the credential into a selectable row, which means "registered, but
   * pick it from the list yourself".
   */
  onRegistered?: (modelProviderSecretId: string | null) => void;
  /**
   * When set, the dialog updates this one existing subscription: the name is
   * fixed (shown read-only) and the title reflects an update. Omit it to
   * register a new subscription with an optional, user-entered name — a blank
   * name registers the caller's default subscription, any other value lets a
   * user keep several Claude Max subscriptions side by side.
   */
  alias?: string;
}

/**
 * Paste-JSON registration flow for a personal Claude Max subscription
 * credential. Self-contained: owns the form state, parse validation, and
 * the {@link api.registerClaudeMax} mutation, so any surface that needs
 * to register a credential can mount this component and pass `open` /
 * `onOpenChange`.
 *
 * A user can register more than one Claude Max subscription by giving each a
 * distinct name (alias); the launch-time key picker then lets them choose
 * which one an agent runs on. Passing the {@link RegisterClaudeMaxDialogProps.alias}
 * prop pins the dialog to an existing subscription so it updates that one in
 * place instead of creating another.
 *
 * Subscriptions are user-tier only — the registered credential is
 * encrypted and stored against the active organization for the calling
 * user. Mutation success invalidates the user credentials list and the
 * Claude Max status query so dependent surfaces (the picker, the
 * settings page) refetch without manual wiring.
 */
export function RegisterClaudeMaxDialog({
  open,
  onOpenChange,
  onRegistered,
  alias,
}: RegisterClaudeMaxDialogProps) {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [aliasInput, setAliasInput] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform>('mac');

  // A caller-supplied `alias` means "update this specific subscription": the
  // name is fixed and not editable. Otherwise the user may name a brand-new one.
  const isUpdate = alias !== undefined;

  const registerMutation = useMutation({
    mutationFn: ({ blob, alias: target }: { blob: ClaudeMaxCredential; alias?: string }) =>
      api.registerClaudeMax(blob, target),
    onSuccess: async (registered) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.userCredentials(orgKey) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.claudeMaxStatus(orgKey) });
      resetForm();
      onOpenChange(false);
      toast.success(
        isUpdate ? 'Claude Max credential updated' : 'Claude Max credential registered',
      );
      // Refresh the accessible-secrets list so a freshly-registered
      // subscription shows up immediately in any open picker. Prefix
      // matches every `types` variant (chat's apiKey list, launch's
      // apiKey+subscription list, …). Awaited before `onRegistered` so a picker
      // reconciling against the pre-registration list can't treat the new id as
      // invalid and overwrite the caller's selection.
      await queryClient.invalidateQueries({
        queryKey: ['user', 'model-provider-secrets', 'accessible', orgKey],
      });
      onRegistered?.(registered.modelProviderSecretId);
    },
    onError: (error: Error) =>
      toast.error('Failed to register credential', { description: error.message }),
  });

  function resetForm() {
    setJsonInput('');
    setParseError(null);
    setAliasInput('');
    setAliasError(null);
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) resetForm();
  }

  function handleRegister() {
    setParseError(null);
    setAliasError(null);

    // Resolve the alias to register under. In update mode it is fixed; when
    // creating, a blank name means the default subscription and any other
    // value is validated against the shared alias rules before submitting.
    let target: string | undefined;
    if (isUpdate) {
      target = alias;
    } else {
      const trimmed = aliasInput.trim();
      if (trimmed.length > 0) {
        const aliasResult = CredentialAliasSchema.safeParse(trimmed);
        if (!aliasResult.success) {
          setAliasError(aliasResult.error.issues[0]?.message ?? 'Invalid name');
          return;
        }
        target = aliasResult.data;
      }
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      setParseError('Invalid JSON. Please paste the contents of your .credentials.json file.');
      return;
    }
    const result = ClaudeMaxCredentialSchema.safeParse(parsed);
    if (!result.success) {
      const issues = result.error.issues.map((i) => i.message).join(', ');
      setParseError(`Invalid credential format: ${issues}`);
      return;
    }
    registerMutation.mutate({ blob: result.data, alias: target });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={credentialDialogContentClass}>
        <DialogHeader>
          <DialogTitle>
            {isUpdate ? 'Update Claude Max Credential' : 'Register Claude Max Credential'}
          </DialogTitle>
          <DialogDescription>
            Run the command below to copy your credential, then paste it here.
          </DialogDescription>
        </DialogHeader>
        <div className={credentialDialogBodyClass}>
          {isUpdate ? (
            <div className="space-y-2">
              <Label>Subscription</Label>
              <p className="text-sm text-muted-foreground">
                Updating{' '}
                {alias === 'default' ? (
                  'your default subscription'
                ) : (
                  <code className="rounded bg-muted px-1 py-0.5 font-mono">{alias}</code>
                )}
                .
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="claude-max-name">Name (optional)</Label>
              <Input
                id="claude-max-name"
                placeholder="e.g. work, personal"
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                autoComplete="off"
              />
              {aliasError ? (
                <p className="text-sm text-destructive">{aliasError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Name this subscription to register more than one and tell them apart when
                  launching agents. Leave blank to use it as your default.
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <div className="flex w-full items-center justify-between">
              <Label>1. Get your credential</Label>
              <Tabs value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
                <TabsList className="h-7" data-tutorial-id="claude-max-platform-tabs">
                  <TabsTrigger value="mac" className="px-2.5 text-xs h-5">
                    macOS
                  </TabsTrigger>
                  <TabsTrigger value="linux" className="px-2.5 text-xs h-5">
                    Linux
                  </TabsTrigger>
                  <TabsTrigger value="windows" className="px-2.5 text-xs h-5">
                    Windows
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <CommandBlock
              commands={PLATFORM_COMMANDS[platform]}
              copyTutorialId="claude-max-copy-command"
            />
            <p className="text-xs text-muted-foreground">
              {platform === 'mac'
                ? 'Reads your Claude credential from the macOS Keychain and copies it to your clipboard.'
                : platform === 'windows'
                  ? 'Reads your Claude credential from the local credentials file. Run in PowerShell.'
                  : 'Reads your Claude credential from the local credentials file.'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="credential-json">2. Paste output here</Label>
            <Textarea
              id="credential-json"
              placeholder='{"claudeAiOauth": {"accessToken": "...", ...}}'
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              className={credentialTextareaClass}
            />
            {parseError && <p className="text-sm text-destructive">{parseError}</p>}
          </div>
          <Alert variant="default" className={credentialInfoAlertClass}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {/* Single <p> keeps <code> inline; AlertDescription's grid otherwise stacks children. */}
              <p>
                After you paste your credential, run{' '}
                <code className={credentialInfoCodeClass}>/login</code> in Claude Code again. Your
                Claude Max refresh token is single-use, so logging in again gives your local Claude
                Code its own credential and stops it from invalidating the one you just gave Reflex.
              </p>
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button
            onClick={handleRegister}
            disabled={registerMutation.isPending || !jsonInput.trim()}
            data-tutorial-id="claude-max-submit"
          >
            {registerMutation.isPending
              ? isUpdate
                ? 'Updating...'
                : 'Registering...'
              : isUpdate
                ? 'Update'
                : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
